import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  ExistingProvider,
  forwardRef,
  HostListener,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {DateHandler} from '../../utils/DateHandler';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {isArray, noop} from 'lodash';
import {Calendar} from "primeng/calendar";
import {ConfirmModalComponent} from "@ispm/layout/components/modals/confirm-modal/confirm-modal.component";
import {ConfirmModalLoaderPayload} from "@ispm/layout/classes/confirm-modal-loader-payload";
import {DynamicModalLoaderService} from "@ispm/layout/services/dynamic-modal-loader.service";
import {IConfirmEditing} from "@ispm/dynamic-form/interfaces/IdynamicFormItem";
import {ModalTypes} from "@ispm/layout/components/modals/contants/modal-types.enum";
import {ConditionalItem} from "@ispm/shared/classes/conditional-item.class";
import {InterpolateService} from "@ispm/shared/services/interpolate.service";
import moment from 'moment';

export const CALENDER_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line
  useExisting: forwardRef( () => CalendarWrapperComponent ),
  multi: true
};

@Component({
  selector: 'gen-calendar-wrapper',
  templateUrl: './calendar-wrapper.component.html',
  styleUrls: ['./calendar-wrapper.component.scss'],
  providers:[CALENDER_CONTROL_VALUE_ACCESSOR]
})
export class CalendarWrapperComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder: string;
  @Input() name: string;
  @Input() required: boolean;
  @Input() inputClass: string;
  @Input() yearNavigator: boolean;
  @Input() showIcon: boolean;
  @Input() appendTo: boolean;
  //upgrading To single range and datePicker calender
  @Input() showTime:boolean;
  @Input() dataType:string = "string"
  @Input() maxDate:Date
  @Input() minDate:Date
  @Input() monthNavigator:boolean
  @Input() selectionMode:string;
  @Input() inline:boolean = false;
  @Input() showButtonBar:boolean = true;
  @Input() usePlatformFormatForDate:boolean = true;

  @Input()
  set confirmEditing(confirm:IConfirmEditing) {
    this._confirmEditing = confirm;
  }

  @Input()
  set filter(value: any) {
    this.model = value.value;
    this.filterChange.emit(value.value);
  }

  get filter(): any {
    return this.model;
  }

  @Input()
  get valid() {
    return this.validateValue;
  }

  set valid(newVal) {
    if (newVal) {
      this.validateValue = newVal;
      this.validateChange.emit(this.validateValue);
    }
  }

  @Input()
  get model() {
    return this.modelValue;
  }

  set model(newVal) {
    if (newVal) {
      if (Array.isArray(newVal)){
        this.modelValue = newVal;
      }

      if (typeof newVal === 'object' && <any> newVal instanceof Date) {
        this.modelValue = (<any> this.setTime(newVal)).toPlatformFormat();
      }
      else {
        this.modelValue = newVal;
      }
    }
    else {
      this.modelValue = null;
    }

    this.valid = this.required ? !!newVal : true;
    // this.onChangeCallback(this.modelValue);
    this.modelChange.emit(this.modelValue);
  }

  @Output() filterChange: EventEmitter<boolean> = new EventEmitter();
  @Output() onFocus: EventEmitter<any> = new EventEmitter();
  @Output() blured: EventEmitter<any> = new EventEmitter();
  @Output() dateSelected: EventEmitter<any> = new EventEmitter();
  @Output() validateChange: EventEmitter<boolean> = new EventEmitter();
  @Output() modelChange: EventEmitter<any> = new EventEmitter();

  @ViewChild(Calendar, {static: false}) private _calendar: Calendar;

  @HostListener('blur') onBlur () {
    this.onTouchedCallback();
  }

  disable:boolean=false;
  fromCalendarOrManually:boolean;
  modelValue: any;
  isModalOpen:boolean = false;
  _confirmEditing:IConfirmEditing
  validateValue: boolean;
  calender:FormControl;

  //  Adding Control Value accessor logic here
  private onTouchedCallback: Function = noop;
  // Function we will call whenever the value changes
  private onChangeCallback: ( val: any ) => void = noop;
  private singleDateSelectedInRangeMode: boolean;

  constructor(
    private el: ElementRef,
    private _zone: NgZone,
    private changeDetector: ChangeDetectorRef,
    private _modalLoader: DynamicModalLoaderService
  ) { }

  ngOnInit() {
    this.fromCalendarOrManually = false;
    this.calender=new FormControl();

    if (this._calendar) {
      const origOnInput = this._calendar.onUserInput;
      this._calendar.onUserInput = (event) => {
        if (this._calendar.overlayVisible) {
          this._calendar.hideOverlay();
        }
        origOnInput.call(this._calendar, event);
      };
    }

    this.calender.valueChanges.subscribe(data=>{
      if (this.selectionMode!=="range")
        if((this._confirmEditing) && (this.model)) {
          if (!this.isModalOpen) {
            this.modalOpen(data);
          }
        }else {
          this.triggerZoneChange(data);
        }
    })

    if (this.inline) { //Handles the issue that when an inline calendar is shown, first click on the calendar does not fire formChange event
      this.blured.emit();
    }
  }

  setTime(date: any) {
    if (!(date instanceof Date)) {
      return;
    }

    date = moment(date).toDate();
    date.setTime(DateHandler.removeTimezone(date).getTime());

    return date;
  }

  onSelect(value) {
    this.fromCalendarOrManually=true;

    if (this.selectionMode!=="range") {
      value = (<any> this.setTime(value)).toPlatformFormat();

      if (!this.isModalOpen) {
        this.triggerZoneChange(value);
      }

      this._calendar.hideOverlay();
    } 
    else {
      if (isArray(this._calendar.value)){
        const selectedDatesCount: number = this._calendar.value.filter(val => !!val).length;
        if (selectedDatesCount === 1){ //In case a single date was selected in Range calendar
          this.singleDateSelectedInRangeMode = true;
          const calendarValue = this.usePlatformFormatForDate ? this._calendar.value[0] && (this._calendar.value[0] as any).toPlatformFormat() : this._calendar.value[0] && (this._calendar.value[0] as any);
          this.dateSelected.emit([calendarValue]);
        }
        else if (selectedDatesCount === 2){ //Two dates were selected
          this.singleDateSelectedInRangeMode = false;
          this.blured.emit();
          this.onClickOutside();
          this._calendar.hideOverlay();
        }      
      }
    }
  }

  private triggerZoneChange(value) {
    this._zone.run(() => {
      this.onChangeCallback(value);
      this.model=value;
      this.changeDetector.detectChanges()
    });
  }

  /**
   * Adding data conversion for range and Single at Dynamic form buildControl method
   * */
  writeValue(value: any) {
    //handling Range
    if (value && this.selectionMode && this.selectionMode === 'range') {
      this.model=[]

      if (Array.isArray(value)) {
        value.map((dates) =>{
          if (dates) {
            this.model.push(new Date(dates))
          }
        })
      }
      else if (value && value.toLowerCase()==='now') {
        this.model=new Date();
      }
      else if (value && value.toLowerCase()==='nextweek') {
        let today= new Date();
        let tempValue=[];
        tempValue.push(new Date(today));
        tempValue.push(new Date(today.setDate(today.getDate() + 6)));
        this.model=tempValue;
      }
      else {
        this.model = [new Date(value), null];
      }
    }
    //handling Single selection mode
    else {
      this.model = value;
    }
    if (!this.singleDateSelectedInRangeMode){ //Do not call patchValue in case only one date was selected in a Range calendaer. Without this the calendar will be closed after selecting only one value
      this.calender.patchValue(this.model,{ emitEvent: false, onlySelf: false });
    }  
  }

  registerOnChange( fn: any ): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched( fn: any ): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?( isDisabled: boolean ): void {
    this.disable=isDisabled;
  }

  onClickOutside() {
    // When date is entered/copied manually it is in a Date format, so a conversion to a string in the correct format is needed
    if (!(this.selectionMode && this.selectionMode !== 'range')) {
      this.singleDateSelectedInRangeMode = false;
      let calendarValue: any;

      if (isArray(this._calendar.value)) {
        calendarValue = this.usePlatformFormatForDate ? this._calendar.value.filter(value => !!value).map(value => (value as any).toPlatformFormat()): this._calendar.value.filter(value => !!value);
      } else {
        calendarValue = this.usePlatformFormatForDate ? this._calendar.value && (this._calendar.value as any).toPlatformFormat() : this._calendar.value && (this._calendar.value as any);
      }

      if (!!calendarValue && !this.fromCalendarOrManually) {
        this.calender.setValue(calendarValue);
        this.triggerZoneChange(calendarValue);
      }

      this.fromCalendarOrManually=false;
    }

    if (this.selectionMode && this.selectionMode.toLowerCase()==="range" && this.calender.value) {
      let valueToPush = this.calender.value.filter(value => !!value);

      if (valueToPush) {
        this.triggerZoneChange(valueToPush);
      }
    }
  }

  clear() {
    this.singleDateSelectedInRangeMode = false;
    this.triggerZoneChange(null);
  }

  modalOpen(value:any) {
    this.isModalOpen = true;
    let conditions = [];
    let interpolationObject = {
      currentValue:value,
      oldValue:this.model
    };
    this._confirmEditing.changeFormula.forEach((condition: ConditionalItem) => {
      let conditionalItem = ConditionalItem.from(interpolationObject,condition);

      if (conditionalItem.verify()){
        conditions.push(true);
      }
      else {
        conditions.push(false);
      }
    })

    if (conditions.every(value => !!value)) {
     const message = InterpolateService.interpolate(this._confirmEditing.modalMessage, interpolationObject);
     let modalSubscriber$ = this._modalLoader.popupModal(
       ConfirmModalComponent,
       new ConfirmModalLoaderPayload(
         this._confirmEditing.modalTitle,
         message,
         this._confirmEditing.confirmButton,
         this._confirmEditing.cancelButton,
         ).setBackdropAllowed(false),
       ModalTypes.ALERT
     ).subscribe(
       (response) => {
         this.isModalOpen = false;
         if (response.payload === 1) {
           this.triggerZoneChange(value);
         } else {
           console.log(this.model);
           this.calender.patchValue(this.model,{ emitEvent: false });
           this.writeValue(this.model)
           this.triggerZoneChange(this.model);
         }
         modalSubscriber$.unsubscribe();
         modalSubscriber$ = null;
       },
       () => {
       },
       () => {
         modalSubscriber$.unsubscribe();
         modalSubscriber$ = null;
       }
     );
   } else {
      this.triggerZoneChange(value);
     this.isModalOpen = false;
   }
  }
}
